/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import clsx from "clsx";
import React, {
  ForwardRefExoticComponent,
  ReactNode,
  RefAttributes,
  RefObject,
  forwardRef,
  useCallback,
  useRef,
} from "react";
import { createPortal } from "react-dom";

import { Backdrop } from "../Backdrop/Backdrop";
import { useCombineRefs, useEscapeKey, useEventOutsideRefs } from "../hooks";
import { useDialog } from "./useDialog";

export type DialogProps = {
  children: ReactNode;
  className?: string;
  id: string;
  onClose?: () => void;
  placement?: "center" | "top";
  producerRef?: RefObject<HTMLElement>;
  role?: string;
  visible?: boolean;
};

export const Dialog: ForwardRefExoticComponent<
  DialogProps & RefAttributes<HTMLDivElement>
> = forwardRef(
  (
    {
      children,
      className,
      id,
      onClose,
      placement = "center",
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      producerRef,
      role = "dialog",
      visible = false,
      ...props
    },
    forwaredRef
  ) => {
    const { isHidden, portalNode, setIsHidden } = useDialog({ visible });
    const dialogRef = useRef<HTMLDivElement>(null);
    const ref = useCombineRefs<HTMLDivElement>(forwaredRef, dialogRef);
    const close = useCallback(() => {
      if (onClose && visible) {
        onClose();
      }
    }, [onClose, visible]);
    useEventOutsideRefs([dialogRef], ["mousedown", "touchstart"], close, {
      deps: [close],
    });
    useEscapeKey(close, visible, [close]);
    const component = (
      <>
        <Backdrop
          onHide={(): void => {
            setIsHidden(true);
          }}
          onShow={(): void => {
            setIsHidden(false);
          }}
          visible={visible}
        />
        <div
          {...props}
          className={clsx(
            "tw-dialog",
            className,
            isHidden && "tw-dialog--none",
            placement === "center" && "tw-dialog--center",
            placement === "top" && "tw-dialog--top",
            visible ? "tw-dialog--visible" : "tw-dialog--hidden"
          )}
          id={id}
          ref={ref}
          role={role}
        >
          {children}
        </div>
      </>
    );
    return portalNode ? createPortal(component, portalNode) : component;
  }
);
